import {
  type CreateTableOverlayConfig,
  createTableOverlay,
} from "@/components/discovery/table-overlays/createTableOverlay";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";

/**
 * Row data shape for the broadcast clips table (AG Grid column fields).
 * Maps from {@link BroadcastClip} when building the datasource.
 */
export type BroadcastClipRowData = {
  ID: string;
  TITLE: string | null;
  TRANSCRIPT_EXCERPT: string | null;
  AIR_DATETIME: string | null;
  DURATION: string | null;
  TOTAL_AUDIENCE: number | null;
  TOTAL_AD_VALUE: number | null;
  NATIONAL_AUDIENCE: number | null;
  NATIONAL_AD_VALUE: number | null;
  NATIONAL_AD_VALUE_CENTS: number | null;
  LOCAL_AUDIENCE: number | null;
  LOCAL_AD_VALUE: number | null;
  LOCAL_AD_VALUE_CENTS: number | null;
  LOCAL_AD_RATE: number | null;
  STATION_NAME: string | null;
  STATION_NETWORK: string | null;
  STATION_MEDIA_TYPE: string | null;
  STATION_PRIMARY_LANGUAGE: string | null;
  STATION_LOCATION_DISPLAY: string | null;
  PROGRAM_TITLE: string | null;
  PROGRAM_CATEGORY: string | null;
  PROGRAM_TYPE: string | null;
};

const useBroadcastClipsTableState: CreateTableOverlayConfig["useTableState"] =
  () => {
    const error = useDiscoveryContentManagementStore(
      (state) => state.broadcastClipsError,
    );
    const isFetching = useDiscoveryContentManagementStore(
      (state) => state.isFetchingBroadcastClips,
    );
    const totalCount = useDiscoveryContentManagementStore(
      (state) => state.totalBroadcastClipsCount,
    );
    const isInitialLoading = useDiscoveryContentManagementStore(
      (state) => state.isBroadcastClipsInitialLoading,
    );
    return { error, isFetching, totalCount, isInitialLoading };
  };

const useBroadcastClipsNoRowsActions: CreateTableOverlayConfig["useNoRowsActions"] =
  () => {
    const searchTerm = useDiscoveryContentManagementStore(
      (state) => state.broadcastClipsSearchTerm,
    );
    const setBroadcastClipsSearchTerm = useDiscoveryContentManagementStore(
      (state) => state.setBroadcastClipsSearchTerm,
    );
    const resetBroadcastClipsTableState = useDiscoveryContentManagementStore(
      (state) => state.resetBroadcastClipsTableState,
    );

    const isAppliedFilters = !!searchTerm;
    const onReset = () => {
      setBroadcastClipsSearchTerm("");
      resetBroadcastClipsTableState();
    };
    return { isAppliedFilters, onReset };
  };

const useBroadcastClipsClearError: CreateTableOverlayConfig["useClearError"] =
  () => {
    const setBroadcastClipsError = useDiscoveryContentManagementStore(
      (state) => state.setBroadcastClipsError,
    );
    return () => setBroadcastClipsError(null);
  };

const broadcastClipsTableOverlayConfig: CreateTableOverlayConfig = {
  useTableState: useBroadcastClipsTableState,
  noRowsMessageWithFilters: "No Broadcast Clips Found for Applied Filters",
  noRowsMessageWithoutFilters: "No Broadcast Clips Found",
  useNoRowsActions: useBroadcastClipsNoRowsActions,
  errorTitle: "Failed to Load Broadcast Clips",
  useClearError: useBroadcastClipsClearError,
};

/** Creates the unified overlay component for the broadcast clips table. */
export const createBroadcastClipsUnifiedOverlay = (onRetry?: () => void) =>
  createTableOverlay(broadcastClipsTableOverlayConfig, onRetry);
